// Localização: frontend/src/pages/Escalas/PreferenciasProfissional.jsx
// Componente para gerenciar preferências de profissionais para escalas

import React, { useState, useEffect } from 'react';
import { X, Save, Settings } from 'lucide-react';
import Modal from '../../components/UI/Modal';
import PrimaryButton from '../../components/Common/PrimaryButton';
import SecondaryButton from '../../components/Common/SecondaryButton';
import Alert from '../../components/UI/Alert';
import LoadingIndicator from '../../components/Common/LoadingIndicator';
import api from '../../api/api';

const PreferenciasProfissional = ({ profissional: profissionalProp, profissionais: profissionaisProp, pacientes: pacientesProp, isOpen, onClose, onSave }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [alertMessage, setAlertMessage] = useState(null);
    const [profissionais, setProfissionais] = useState(profissionaisProp || []);
    const [pacientes, setPacientes] = useState(pacientesProp || []);
    const [profissional, setProfissional] = useState(profissionalProp || null);
    const [modoMultiplo, setModoMultiplo] = useState(false);
    const [profissionaisSelecionados, setProfissionaisSelecionados] = useState([]);
    const [pacienteSelecionado, setPacienteSelecionado] = useState(null);
    
    // Estados para preferências básicas
    const [cargaHorariaSemanal, setCargaHorariaSemanal] = useState(40);
    const [cargaHorariaDiaria, setCargaHorariaDiaria] = useState(12);
    const [intervaloDescanso, setIntervaloDescanso] = useState(12);
    
    // Estados para regras específicas de turno
    const [duracaoTurno, setDuracaoTurno] = useState('');
    const [horarioTurnoInicio, setHorarioTurnoInicio] = useState('');
    const [horarioTurnoFim, setHorarioTurnoFim] = useState('');
    const [turnoObrigatorio, setTurnoObrigatorio] = useState('');
    const [preferenciaTipoPlantao, setPreferenciaTipoPlantao] = useState([]);
    
    // Estados para dias da semana
    const [diasPreferidos, setDiasPreferidos] = useState([]);
    const [diasIndisponiveis, setDiasIndisponiveis] = useState([]);

    const diasSemana = [
        { value: 1, label: 'Segunda-feira' },
        { value: 2, label: 'Terça-feira' },
        { value: 3, label: 'Quarta-feira' },
        { value: 4, label: 'Quinta-feira' },
        { value: 5, label: 'Sexta-feira' },
        { value: 6, label: 'Sábado' },
        { value: 7, label: 'Domingo' }
    ];

    const tiposPlantao = [
        { value: 'diurno', label: 'Diurno' },
        { value: 'noturno', label: 'Noturno' },
        { value: 'integral', label: 'Integral' },
        { value: 'madrugada', label: 'Madrugada' }
    ];

    useEffect(() => {
        if (isOpen) {
            if (profissionalProp) {
                setProfissional(profissionalProp);
                setModoMultiplo(false);
            } else {
                setModoMultiplo(true);
            }
            if (!profissionaisProp || profissionaisProp.length === 0) {
                carregarProfissionais();
            } else {
                setProfissionais(profissionaisProp);
            }
            if (!pacientesProp || pacientesProp.length === 0) {
                carregarPacientes();
            } else {
                setPacientes(pacientesProp);
            }
        }
    }, [isOpen, profissionalProp, profissionaisProp, pacientesProp]);

    const carregarPacientes = async () => {
        try {
            const response = await api.get('/pacientes');
            // Filtrar apenas pacientes ativos (excluir cancelados e excluídos)
            const pacientesAtivos = (response.data || []).filter(
                paciente => paciente.status === 'Ativo'
            );
            setPacientes(pacientesAtivos);
        } catch (error) {
            console.error('Erro ao carregar pacientes:', error);
        }
    };

    useEffect(() => {
        if (isOpen && profissional) {
            carregarPreferencias();
        } else if (isOpen && !profissional) {
            resetarFormulario();
        }
    }, [isOpen, profissional]);

    const carregarProfissionais = async () => {
        try {
            const response = await api.get('/usuarios');
            const profs = (response.data || []).filter(u => 
                ['enfermeiro', 'medico', 'fisioterapeuta', 'cuidador', 'profissional'].includes(
                    u.role?.toLowerCase()
                )
            );
            setProfissionais(profs);
        } catch (error) {
            console.error('Erro ao carregar profissionais:', error);
        }
    };

    const carregarPreferencias = async () => {
        if (!profissional?.id) return;
        
        setLoading(true);
        setAlertMessage(null);
        
        try {
            const response = await api.get(`/escalas/preferencias/${profissional.id}`);
            const pref = response.data?.data;
            
            if (pref) {
                setCargaHorariaSemanal(pref.carga_horaria_semanal_maxima || 40);
                setCargaHorariaDiaria(pref.carga_horaria_diaria_maxima || 12);
                setIntervaloDescanso(pref.intervalo_descanso_minimo || 12);
                setDuracaoTurno(pref.duracao_turno_horas || '');
                setHorarioTurnoInicio(pref.horario_turno_inicio || '');
                setHorarioTurnoFim(pref.horario_turno_fim || '');
                setTurnoObrigatorio(pref.turno_obrigatorio || '');
                setPreferenciaTipoPlantao(pref.preferencia_tipo_plantao || []);
                setDiasPreferidos(pref.dias_semana_preferidos || []);
                setDiasIndisponiveis(pref.dias_semana_indisponiveis || []);
            } else {
                // Resetar para valores padrão
                resetarFormulario();
            }
        } catch (error) {
            console.error('Erro ao carregar preferências:', error);
            setAlertMessage({
                type: 'error',
                message: 'Erro ao carregar preferências do profissional'
            });
        } finally {
            setLoading(false);
        }
    };

    const resetarFormulario = () => {
        setCargaHorariaSemanal(40);
        setCargaHorariaDiaria(12);
        setIntervaloDescanso(12);
        setDuracaoTurno('');
        setHorarioTurnoInicio('');
        setHorarioTurnoFim('');
        setTurnoObrigatorio('');
        setPreferenciaTipoPlantao([]);
        setDiasPreferidos([]);
        setDiasIndisponiveis([]);
    };

    const handleSalvar = async () => {
        setSaving(true);
        setAlertMessage(null);

        try {
            // Modo múltiplo: salvar para vários profissionais
            if (modoMultiplo) {
                if (profissionaisSelecionados.length === 0) {
                    setAlertMessage({
                        type: 'error',
                        message: 'Selecione pelo menos um profissional'
                    });
                    setSaving(false);
                    return;
                }

                const profissionaisData = profissionaisSelecionados.map(profId => ({
                    usuario_id: profId,
                    carga_horaria_semanal_maxima: parseInt(cargaHorariaSemanal),
                    carga_horaria_diaria_maxima: parseInt(cargaHorariaDiaria),
                    intervalo_descanso_minimo: parseInt(intervaloDescanso),
                    duracao_turno_horas: duracaoTurno ? parseInt(duracaoTurno) : null,
                    horario_turno_inicio: horarioTurnoInicio || null,
                    horario_turno_fim: horarioTurnoFim || null,
                    turno_obrigatorio: turnoObrigatorio || null,
                    preferencia_tipo_plantao: preferenciaTipoPlantao,
                    dias_semana_preferidos: diasPreferidos,
                    dias_semana_indisponiveis: diasIndisponiveis
                }));

                const dados = {
                    profissionais: profissionaisData,
                    paciente_id: pacienteSelecionado || null
                };

                const response = await api.post('/escalas/preferencias', dados);
                
                setAlertMessage({
                    type: 'success',
                    message: `Preferências salvas para ${response.data.data?.length || profissionaisSelecionados.length} profissional(is)!`
                });
            } else {
                // Modo único
                if (!profissional?.id) {
                    setAlertMessage({
                        type: 'error',
                        message: 'Selecione um profissional'
                    });
                    setSaving(false);
                    return;
                }

                const dados = {
                    usuario_id: profissional.id,
                    paciente_id: pacienteSelecionado || null,
                    carga_horaria_semanal_maxima: parseInt(cargaHorariaSemanal),
                    carga_horaria_diaria_maxima: parseInt(cargaHorariaDiaria),
                    intervalo_descanso_minimo: parseInt(intervaloDescanso),
                    duracao_turno_horas: duracaoTurno ? parseInt(duracaoTurno) : null,
                    horario_turno_inicio: horarioTurnoInicio || null,
                    horario_turno_fim: horarioTurnoFim || null,
                    turno_obrigatorio: turnoObrigatorio || null,
                    preferencia_tipo_plantao: preferenciaTipoPlantao,
                    dias_semana_preferidos: diasPreferidos,
                    dias_semana_indisponiveis: diasIndisponiveis
                };

                await api.post('/escalas/preferencias', dados);
                
                setAlertMessage({
                    type: 'success',
                    message: 'Preferências salvas com sucesso!'
                });
            }

            if (onSave) {
                onSave();
            }

            setTimeout(() => {
                onClose();
            }, 1500);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Erro ao salvar preferências';
            setAlertMessage({
                type: 'error',
                message: errorMessage
            });
        } finally {
            setSaving(false);
        }
    };

    const toggleDiaPreferido = (dia) => {
        setDiasPreferidos(prev => 
            prev.includes(dia) 
                ? prev.filter(d => d !== dia)
                : [...prev, dia]
        );
    };

    const toggleDiaIndisponivel = (dia) => {
        setDiasIndisponiveis(prev => 
            prev.includes(dia) 
                ? prev.filter(d => d !== dia)
                : [...prev, dia]
        );
    };

    const toggleTipoPlantao = (tipo) => {
        setPreferenciaTipoPlantao(prev => 
            prev.includes(tipo)
                ? prev.filter(t => t !== tipo)
                : [...prev, tipo]
        );
    };

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={profissional ? `Configurar Preferências - ${profissional.nome_completo || profissional.nome}` : "Configurar Preferências de Profissionais"}
            size="xl"
        >
            {!profissional && !modoMultiplo ? (
                <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                            Selecione um profissional para configurar suas preferências de escala.
                        </p>
                    </div>

                    {/* Seleção de Paciente (Opcional) */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Paciente Específico (Opcional)
                        </label>
                        <select
                            value={pacienteSelecionado || ''}
                            onChange={(e) => setPacienteSelecionado(e.target.value ? parseInt(e.target.value) : null)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            <option value="">Nenhum (Preferência Geral)</option>
                            {pacientes.map((paciente) => (
                                <option key={paciente.id} value={paciente.id}>
                                    {paciente.nome_completo || paciente.nome}
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                            Se selecionar um paciente, as preferências serão aplicadas apenas quando o profissional trabalhar com este paciente.
                        </p>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Selecionar Profissional
                        </label>
                        <select
                            value={profissional?.id || ''}
                            onChange={(e) => {
                                const profId = parseInt(e.target.value);
                                const prof = profissionais.find(p => p.id === profId);
                                setProfissional(prof || null);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            <option value="">Selecione um profissional...</option>
                            {profissionais.map((prof) => (
                                <option key={prof.id} value={prof.id}>
                                    {prof.nome_completo || prof.nome} ({prof.role})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Botão para alternar modo */}
                    <div className="flex justify-between items-center pt-2 border-t">
                        <button
                            type="button"
                            onClick={() => setModoMultiplo(true)}
                            className="text-sm text-primary-600 hover:text-primary-700"
                        >
                            Configurar múltiplos profissionais
                        </button>
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t">
                        <SecondaryButton onClick={onClose}>
                            Fechar
                        </SecondaryButton>
                    </div>
                </div>
            ) : modoMultiplo && !profissional ? (
                <div className="space-y-4">
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <p className="text-sm text-purple-800">
                            Configure preferências para múltiplos profissionais de uma vez. As mesmas regras serão aplicadas a todos os selecionados.
                        </p>
                    </div>

                    {/* Seleção de Paciente (Opcional) */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Paciente Específico (Opcional)
                        </label>
                        <select
                            value={pacienteSelecionado || ''}
                            onChange={(e) => setPacienteSelecionado(e.target.value ? parseInt(e.target.value) : null)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            <option value="">Nenhum (Preferência Geral)</option>
                            {pacientes.map((paciente) => (
                                <option key={paciente.id} value={paciente.id}>
                                    {paciente.nome_completo || paciente.nome}
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                            Se selecionar um paciente, as preferências serão aplicadas apenas quando os profissionais trabalharem com este paciente.
                        </p>
                    </div>
                    
                    {/* Seleção Múltipla de Profissionais */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Selecionar Profissionais *
                        </label>
                        <select
                            multiple
                            value={profissionaisSelecionados}
                            onChange={(e) => {
                                const selected = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                                setProfissionaisSelecionados(selected);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[150px]"
                            size="6"
                        >
                            {profissionais.map((prof) => (
                                <option key={prof.id} value={prof.id}>
                                    {prof.nome_completo || prof.nome} ({prof.role})
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                            Segure Ctrl/Cmd para selecionar múltiplos profissionais. {profissionaisSelecionados.length} selecionado(s).
                        </p>
                    </div>

                    {/* Botão para alternar modo */}
                    <div className="flex justify-between items-center pt-2 border-t">
                        <button
                            type="button"
                            onClick={() => {
                                setModoMultiplo(false);
                                setProfissionaisSelecionados([]);
                            }}
                            className="text-sm text-primary-600 hover:text-primary-700"
                        >
                            Configurar profissional individual
                        </button>
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t">
                        <SecondaryButton onClick={onClose}>
                            Cancelar
                        </SecondaryButton>
                        <PrimaryButton
                            onClick={handleSalvar}
                            disabled={saving || profissionaisSelecionados.length === 0}
                            className="flex items-center gap-2"
                        >
                            {saving ? (
                                <>
                                    <LoadingIndicator small />
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    Salvar para {profissionaisSelecionados.length} Profissional(is)
                                </>
                            )}
                        </PrimaryButton>
                    </div>
                </div>
            ) : loading ? (
                <LoadingIndicator message="Carregando preferências..." />
            ) : (
                <div className="space-y-6">
                    {alertMessage && (
                        <Alert
                            type={alertMessage.type}
                            message={alertMessage.message}
                            onClose={() => setAlertMessage(null)}
                        />
                    )}

                    {/* Seleção de Paciente (Opcional) - Modo Individual */}
                    {!modoMultiplo && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Paciente Específico (Opcional)
                            </label>
                            <select
                                value={pacienteSelecionado || ''}
                                onChange={(e) => setPacienteSelecionado(e.target.value ? parseInt(e.target.value) : null)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                                <option value="">Nenhum (Preferência Geral)</option>
                                {pacientes.map((paciente) => (
                                    <option key={paciente.id} value={paciente.id}>
                                        {paciente.nome_completo || paciente.nome}
                                    </option>
                                ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                                Se selecionar um paciente, as preferências serão aplicadas apenas quando o profissional trabalhar com este paciente.
                            </p>
                        </div>
                    )}

                    {/* Regras Específicas de Turno */}
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-purple-900 mb-3 flex items-center gap-2">
                            <Settings className="w-5 h-5" />
                            Regras Específicas de Turno
                        </h3>
                        <p className="text-sm text-purple-800 mb-4">
                            Configure regras obrigatórias para este profissional. Ex: Turno noite 12h, Turno manhã 6h
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Duração do Turno (horas)
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="24"
                                    value={duracaoTurno}
                                    onChange={(e) => setDuracaoTurno(e.target.value)}
                                    placeholder="Ex: 6, 8, 12"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Deixe vazio para usar duração padrão
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Tipo de Turno Obrigatório
                                </label>
                                <select
                                    value={turnoObrigatorio}
                                    onChange={(e) => setTurnoObrigatorio(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                >
                                    <option value="">Nenhum (flexível)</option>
                                    {tiposPlantao.map(tipo => (
                                        <option key={tipo.value} value={tipo.value}>
                                            {tipo.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Horário Início do Turno
                                </label>
                                <input
                                    type="time"
                                    value={horarioTurnoInicio}
                                    onChange={(e) => setHorarioTurnoInicio(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Horário Fim do Turno
                                </label>
                                <input
                                    type="time"
                                    value={horarioTurnoFim}
                                    onChange={(e) => setHorarioTurnoFim(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Preferências Básicas */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Preferências Básicas</h3>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Carga Horária Semanal Máxima
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="168"
                                    value={cargaHorariaSemanal}
                                    onChange={(e) => setCargaHorariaSemanal(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Carga Horária Diária Máxima
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="24"
                                    value={cargaHorariaDiaria}
                                    onChange={(e) => setCargaHorariaDiaria(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Intervalo Descanso Mínimo (horas)
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    max="24"
                                    value={intervaloDescanso}
                                    onChange={(e) => setIntervaloDescanso(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Tipos de Plantão Preferidos */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Tipos de Plantão Preferidos</h3>
                        <div className="flex flex-wrap gap-2">
                            {tiposPlantao.map(tipo => (
                                <button
                                    key={tipo.value}
                                    type="button"
                                    onClick={() => toggleTipoPlantao(tipo.value)}
                                    className={`px-4 py-2 rounded-lg border transition-colors ${
                                        preferenciaTipoPlantao.includes(tipo.value)
                                            ? 'bg-primary-600 text-white border-primary-600'
                                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    {tipo.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Dias da Semana */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Dias Preferidos</h3>
                            <div className="space-y-2">
                                {diasSemana.map(dia => (
                                    <label key={dia.value} className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={diasPreferidos.includes(dia.value)}
                                            onChange={() => toggleDiaPreferido(dia.value)}
                                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="text-sm text-gray-700">{dia.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Dias Indisponíveis</h3>
                            <div className="space-y-2">
                                {diasSemana.map(dia => (
                                    <label key={dia.value} className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={diasIndisponiveis.includes(dia.value)}
                                            onChange={() => toggleDiaIndisponivel(dia.value)}
                                            className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                                        />
                                        <span className="text-sm text-gray-700">{dia.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Botões */}
                    <div className="flex justify-end gap-2 pt-4 border-t">
                        <SecondaryButton onClick={onClose}>
                            Cancelar
                        </SecondaryButton>
                        <PrimaryButton
                            onClick={handleSalvar}
                            disabled={saving}
                            className="flex items-center gap-2"
                        >
                            {saving ? (
                                <>
                                    <LoadingIndicator small />
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    Salvar Preferências
                                </>
                            )}
                        </PrimaryButton>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default PreferenciasProfissional;

